import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { api, ApiError, imageUrl } from '../lib/api'
import { CHECK_THE_PACKET, conflictLine, conflictOf, conflictShort, worthAsking, type Conflict } from '../lib/conflicts'
import { useToast } from '../lib/toast'
import { addWeeks, dayDate, formatDayDate, formatWeekRange, isSameWeek, mondayOf, relativeWeekLabel } from '../lib/dates'
import { Thumb } from './Thumb'
import { ImageLightbox } from './ImageLightbox'
import { Modal } from './Modal'
import { PantryDock } from './PantryDock'
import { ItemPicker } from './ItemPicker'
import { DaySettings } from './DaySettings'
import { ItemEditor } from './ItemEditor'
import { RepeatDay } from './RepeatDay'
import { Menu, type MenuItem } from './Menu'
import { acceptingCategoryIds, canPlace } from '@shared/placement'
import { CardMouseSensor, CardTouchSensor } from '../lib/sensors'
import { MealPicker } from './MealPicker'
import { ReviewControl } from './ReviewControl'
import { ReviewComposer } from './ReviewComposer'
import { NotesPanel } from './NotesPanel'
import { DAY_NAMES, DAY_STATUSES, type Bootstrap, type Category, type Child, type DayStatus, type Item, type Lunch, type Meal, type ProfileAllergen, type SiblingClash, type Week, type WeekSummary } from '@shared/types'
import type { Delivery } from '../lib/api'
import { NARROW, useMediaQuery } from '../lib/media'

interface Props {
  boot: Bootstrap
  weeks: WeekSummary[]
  weekStart: string
  week: Week | null
  loading: boolean
  onNavigate: (startDate: string) => void
  onWeek: (week: Week) => void
  onWeeksChanged: () => void
  onMealsChanged: (meals: Meal[]) => void
  onItemsChanged: (items: Item[]) => void
  onManageMeals: () => void
  /** The other children in the family, for "give this lunch to". */
  siblings: Child[]
  onError: (err: unknown, fallback: string) => void
}

/** Which day is being handed to a sibling, and what they need told about it. */
interface GiveState {
  dayOfWeek: number | null
  child: Child
  clashes: SiblingClash[]
}

/**
 * A name typed into a search box, tidied just enough to sit in a pantry
 * alongside "Carrot Sticks": "okra" becomes "Okra". Anything already carrying
 * a capital is left exactly as typed, so "PB&J" survives. It is only a
 * prefill -- the editor is open, and the field can be changed.
 */
const asFoodName = (typed: string) =>
  /[A-Z]/.test(typed) ? typed : typed.replace(/^\p{Ll}/u, (c) => c.toUpperCase())

/** A day's placements as the API wants them back, optionally dropping one. */
const keep = (lunch: Lunch, drop?: string) =>
  lunch.items.filter((i) => i.id !== drop).map((i) => ({ itemId: i.itemId, categoryId: i.categoryId }))

/**
 * The slot whose picker is open: which day, which slot, and the placement
 * that gives way when one is being replaced. `query` prefills the search,
 * which matters on the way back from a new food someone thought better of.
 */
interface Picking {
  dayOfWeek: number
  category: Category
  replace?: string
  query?: string
}

/**
 * The item editor, opened from the board rather than the Pantry tab.
 *
 * A new food starts in `categoryId` under `name`, both of which the editor
 * may still change, and `slot` says which slot it was invented in -- where it
 * goes once saved -- or null when it came from the dock and belongs nowhere
 * in particular.
 */
type FoodEdit =
  | { kind: 'new'; categoryId: string; name: string; slot: Picking | null }
  | { kind: 'edit'; item: Item }

/** What is currently being dragged, for the overlay and slot highlighting. */
type Dragging =
  | { kind: 'pantry'; item: Item }
  | { kind: 'placed'; item: Item; placementId: string; dayOfWeek: number; categoryId: string }
  | null

export function PlanView({
  boot,
  weeks,
  weekStart,
  week,
  loading,
  onNavigate,
  onWeek,
  onWeeksChanged,
  onMealsChanged,
  onItemsChanged,
  onManageMeals,
  siblings,
  onError,
}: Props) {
  const toast = useToast()
  /** Repeat one day's lunch onto others: the source weekday, or null when closed. */
  const [repeatFrom, setRepeatFrom] = useState<number | null>(null)
  /* Which day is being given away, and the confirm when the sibling's own
     allergens have something to say about it. */
  const [giveDay, setGiveDay] = useState<number | null>(null)
  const [giving, setGiving] = useState<GiveState | null>(null)

  /**
   * Hand a day, or the whole week, to a sibling.
   *
   * The server checks the receiving child's allergies, restrictions and
   * school rules first and refuses with 'needs_confirming' when any of them
   * is worth stopping for. That refusal is the question, not a failure: show
   * it, and send the same request back with the answer.
   */
  const giveTo = useCallback(
    async (child: Child, dayOfWeek: number | null, confirm = false) => {
      if (!week) return
      try {
        const res = await api.copyWeekTo(week.id, child.id, { dayOfWeek, confirm })
        setGiving(null)
        setGiveDay(null)
        const what = dayOfWeek === null ? 'The week' : DAY_NAMES[dayOfWeek - 1]
        const flagged = res.clashes.length
        toast.notify(
          flagged > 0
            ? `${what} is on ${res.childName}'s board. ${flagged} thing${flagged === 1 ? '' : 's'} to check for them.`
            : `${what} is on ${res.childName}'s board.`,
        )
        onWeeksChanged()
      } catch (err) {
        if (err instanceof ApiError && err.code === 'needs_confirming') {
          const detail = err.body as { clashes?: SiblingClash[] } | null
          setGiving({ dayOfWeek, child, clashes: detail?.clashes ?? [] })
          return
        }
        onError(err, 'Could not give them that lunch.')
      }
    },
    [week, toast, onWeeksChanged, onError],
  )
  const [repeatBusy, setRepeatBusy] = useState(false)
  /** The first-week nudge to repeat a finished day, once dismissed. */
  const [repeatDismissed, setRepeatDismissed] = useState(false)
  /** The item editor on the board: a food being invented, or one being fixed. */
  const [foodEdit, setFoodEdit] = useState<FoodEdit | null>(null)
  /*
   * Where to go back to if that editor is cancelled: the picker the food was
   * invented in, query and all. Saving clears it, because the food is already
   * on its way into the slot and putting the picker back would undo the trip.
   * Both paths end in the editor's onClose, so which one happened is recorded
   * rather than inferred.
   */
  const pickerBehind = useRef<Picking | null>(null)
  /*
   * Phones and tablets show one day at a time under a five-day strip. The
   * day in focus starts on today when the week on screen is this week, and
   * otherwise on the first day with something still to choose.
   */
  const narrow = useMediaQuery(NARROW)
  const [focusDay, setFocusDay] = useState<number>(() => {
    const d = new Date().getDay()
    return isSameWeek(weekStart) && d >= 1 && d <= 5 ? d : 1
  })

  /** Most recently packed foods, for the dock's Recent tab. Refreshed as the week changes. */
  const [recentIds, setRecentIds] = useState<string[]>([])
  useEffect(() => {
    let live = true
    api
      .recentItems()
      .then((ids) => {
        if (live) setRecentIds(ids)
      })
      .catch(() => {
        // The dock falls back to All; nothing to tell the user.
      })
    return () => {
      live = false
    }
  }, [week])
  const [dragging, setDragging] = useState<Dragging>(null)
  /** The slot being filled by tap; `replace` is the placement that gives way. */
  const [picker, setPicker] = useState<Picking | null>(null)
  const [settingsDay, setSettingsDay] = useState<Lunch | null>(null)
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<Item | null>(null)
  // null = closed; { dayOfWeek: null } = the week's own notes
  const [notesFor, setNotesFor] = useState<{ dayOfWeek: number | null } | null>(null)
  // Which day the saved-meal picker is open for, by weekday rather than by
  // lunch: applying a meal returns a fresh week, and the picker stays open.
  const [mealsForDay, setMealsForDay] = useState<number | null>(null)
  /** A placement held back until someone confirms it. Never a block, only a pause. */
  const [ask, setAsk] = useState<{ item: Item; conflict: Conflict; run: () => void } | null>(null)
  const [reviewBusy, setReviewBusy] = useState(false)
  const [composing, setComposing] = useState(false)
  const itemsById = useMemo(() => new Map(boot.items.map((i) => [i.id, i])), [boot.items])

  /*
   * Which slots the thing in hand may land in. Null when nothing is being
   * dragged. The board dims by this, the pantry recedes by this and the drop
   * itself is decided by the same rule, so a lit slot always accepts.
   */
  const dropTargets = useMemo(
    () => (dragging ? acceptingCategoryIds(boot.categories, dragging.item.categoryId) : null),
    [dragging, boot.categories],
  )

  /*
   * Mouse and touch are deliberately different gestures for the same thing.
   *
   * A mouse drags as soon as it has moved 6px, which also leaves a plain click
   * on the ✕ a click. A finger has to hold still for a moment first: the cards
   * cover most of the board on a phone, so a finger that dragged immediately
   * would leave nothing to scroll with. Moving before the delay is up is a
   * scroll and cancels the drag; staying put picks the card up.
   */
  const sensors = useSensors(
    useSensor(CardMouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(CardTouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  )

  /* ---- mutations ------------------------------------------------------- */

  /*
   * A day is saved as a whole, so every call below has to restate the
   * placements that are staying. They carry their category with them rather
   * than being re-derived from the item: a Side sitting in the Snack slot is
   * only a Snack because the placement says so, and dropping that on an
   * unrelated edit would silently file it back home.
   */
  const setDayItems = useCallback(
    async (dayOfWeek: number, placements: { itemId: string; categoryId: string }[]) => {
      if (!week) return
      try {
        onWeek(await api.setDayItems(week.id, dayOfWeek, placements))
      } catch (err) {
        onError(err, 'Could not save that change.')
      }
    },
    [week, onWeek, onError],
  )

  const addItem = useCallback(
    (dayOfWeek: number, itemId: string, categoryId: string) => {
      const lunch = week?.lunches.find((l) => l.dayOfWeek === dayOfWeek)
      if (!lunch) return
      void setDayItems(dayOfWeek, [...keep(lunch), { itemId, categoryId }])
    },
    [week, setDayItems],
  )

  const removeItem = useCallback(
    (dayOfWeek: number, placementId: string) => {
      const lunch = week?.lunches.find((l) => l.dayOfWeek === dayOfWeek)
      if (!lunch) return
      void setDayItems(dayOfWeek, keep(lunch, placementId))
    },
    [week, setDayItems],
  )

  /** Swap one placed food for another, in the same slot. */
  const replaceItem = useCallback(
    (dayOfWeek: number, placementId: string, itemId: string) => {
      const lunch = week?.lunches.find((l) => l.dayOfWeek === dayOfWeek)
      if (!lunch) return
      void setDayItems(
        dayOfWeek,
        lunch.items.map((i) => ({ itemId: i.id === placementId ? itemId : i.itemId, categoryId: i.categoryId })),
      )
    },
    [week, setDayItems],
  )

  /** Packing, pizza or skip, straight from the day header. */
  const setStatus = useCallback(
    async (dayOfWeek: number, status: DayStatus) => {
      const lunch = week?.lunches.find((l) => l.dayOfWeek === dayOfWeek)
      if (!week || !lunch || lunch.status === status) return
      try {
        onWeek(await api.setDay(week.id, dayOfWeek, { status, label: lunch.label }))
      } catch (err) {
        onError(err, 'Could not update that day.')
      }
    },
    [week, onWeek, onError],
  )

  /**
   * Copy a day's foods into other days' empty slots, one save per day. What
   * a day already has stays; only gaps are filled, so nobody's choice is
   * overwritten by a repeat.
   */
  const repeatDay = useCallback(
    async (sourceDay: number, days: number[]) => {
      const source = week?.lunches.find((l) => l.dayOfWeek === sourceDay)
      if (!week || !source || repeatBusy) return
      setRepeatBusy(true)
      try {
        let fresh: Week = week
        for (const d of days) {
          const target = fresh.lunches.find((l) => l.dayOfWeek === d)
          if (!target) continue
          const empty = new Set(dayFill(target, boot.categories).empty.map((c) => c.id))
          const adds = source.items.filter((i) => empty.has(i.categoryId)).map((i) => ({ itemId: i.itemId, categoryId: i.categoryId }))
          if (adds.length === 0) continue
          fresh = await api.setDayItems(week.id, d, [...keep(target), ...adds])
        }
        onWeek(fresh)
        setRepeatFrom(null)
        setRepeatDismissed(true)
        toast.notify(`${DAY_NAMES[sourceDay - 1]}'s lunch repeated on ${days.length} day${days.length === 1 ? '' : 's'}.`)
      } catch (err) {
        onError(err, 'Could not repeat that day.')
      } finally {
        setRepeatBusy(false)
      }
    },
    [week, repeatBusy, boot.categories, onWeek, toast, onError],
  )

  /**
   * Move a placement into another slot on the same day -- Side to Snack and
   * back. One save: nothing leaves the day, only the slot it claims changes.
   */
  const refileItem = useCallback(
    (dayOfWeek: number, placementId: string, categoryId: string) => {
      const lunch = week?.lunches.find((l) => l.dayOfWeek === dayOfWeek)
      if (!lunch) return
      void setDayItems(
        dayOfWeek,
        lunch.items.map((i) => ({
          itemId: i.itemId,
          categoryId: i.id === placementId ? categoryId : i.categoryId,
        })),
      )
    },
    [week, setDayItems],
  )

  /** Move a placed item to another day, in one round trip per day touched. */
  const moveItem = useCallback(
    async (
      fromDay: number,
      placementId: string,
      toDay: number,
      itemId: string,
      categoryId: string,
    ) => {
      if (!week || fromDay === toDay) return
      const from = week.lunches.find((l) => l.dayOfWeek === fromDay)
      const to = week.lunches.find((l) => l.dayOfWeek === toDay)
      if (!from || !to) return
      try {
        await api.setDayItems(week.id, fromDay, keep(from, placementId))
        onWeek(await api.setDayItems(week.id, toDay, [...keep(to), { itemId, categoryId }]))
      } catch (err) {
        onError(err, 'Could not move that item.')
      }
    },
    [week, onWeek, onError],
  )

  /* ---- saved meals ------------------------------------------------------ */

  /**
   * Fill a day from a saved meal. The meals list is refetched afterwards
   * because applying one bumps how recently it was used, which is what orders
   * the picker.
   */
  const applyMeal = useCallback(
    async (dayOfWeek: number, mealId: string, mode: 'replace' | 'add') => {
      if (!week) return
      try {
        onWeek(await api.applyMeal(week.id, dayOfWeek, mealId, mode))
        onMealsChanged(await api.listMeals())
        toast.notify(`${DAY_NAMES[dayOfWeek - 1]} filled.`)
      } catch (err) {
        onError(err, 'Could not apply that meal.')
      }
    },
    [week, onWeek, onMealsChanged, toast, onError],
  )

  const saveDayAsMeal = useCallback(
    async (dayOfWeek: number, name: string): Promise<boolean> => {
      if (!week) return false
      try {
        const { meals } = await api.createMeal({ name, fromWeekId: week.id, dayOfWeek })
        onMealsChanged(meals)
        toast.notify(`Saved “${name}”.`)
        return true
      } catch (err) {
        // A refused name is worth retyping over, so say so and keep the form.
        onError(err, 'Could not save that meal.')
        return false
      }
    },
    [week, onMealsChanged, toast, onError],
  )

  const toggleFavorite = useCallback(
    async (meal: Meal) => {
      try {
        onMealsChanged(await api.updateMeal(meal.id, { favorite: !meal.favorite }))
      } catch (err) {
        onError(err, 'Could not update that meal.')
      }
    },
    [onMealsChanged, onError],
  )

  /* ---- review handoff --------------------------------------------------- */

  /**
   * Say what actually happened. "Sent for review" when nothing left the Worker
   * is the kind of small lie that costs you the feature: the next time it
   * silently fails, nobody checks.
   */
  const reportDeliveries = useCallback(
    (deliveries: Delivery[], nothingToSend: string) => {
      const sent = deliveries.filter((d) => d.ok && !d.duplicate)
      const failed = deliveries.filter((d) => !d.ok)
      if (failed.length > 0) {
        toast.warn(`Saved, but the email to ${failed.map((d) => d.recipient).join(', ')} failed.`)
      } else if (sent.length > 0) {
        toast.notify(`Emailed ${sent.map((d) => d.recipient).join(' and ')}.`)
      } else {
        toast.notify(nothingToSend)
      }
    },
    [toast],
  )

  const requestReview = useCallback(
    async (payload: { message: string; editNoteId: string | null }) => {
      if (!week || reviewBusy) return
      setReviewBusy(true)
      try {
        const { week: fresh, deliveries } = await api.requestReview(week.id, payload)
        onWeek(fresh)
        setComposing(false)
        reportDeliveries(deliveries, 'Marked ready for review — nobody has review emails on yet.')
      } catch (err) {
        // The composer stays open, so whatever was typed is not lost.
        onError(err, 'Could not send that for review.')
      } finally {
        setReviewBusy(false)
      }
    },
    [week, reviewBusy, onWeek, reportDeliveries, onError],
  )

  const approveReview = useCallback(
    async (comment: string) => {
      if (!week || reviewBusy) return
      setReviewBusy(true)
      try {
        const { week: fresh, deliveries } = await api.approveReview(week.id, comment)
        onWeek(fresh)
        reportDeliveries(deliveries, 'Approved.')
      } catch (err) {
        onError(err, 'Could not approve that week.')
      } finally {
        setReviewBusy(false)
      }
    },
    [week, reviewBusy, onWeek, reportDeliveries, onError],
  )

  const reopenReview = useCallback(async () => {
    if (!week || reviewBusy) return
    setReviewBusy(true)
    try {
      onWeek(await api.reopenReview(week.id))
    } catch (err) {
      onError(err, 'Could not reopen that week.')
    } finally {
      setReviewBusy(false)
    }
  }, [week, reviewBusy, onWeek, onError])

  /* ---- drag plumbing --------------------------------------------------- */

  /*
   * Placing a food that runs into a severe allergy or a school rule stops and
   * asks. It never blocks: a parent may have bought the safe version, and the
   * app has no way to know that. worthAsking() says why an intolerance gets a
   * flag on the tile but no dialog.
   */
  const askFirst = useCallback(
    (item: Item | undefined, run: () => void) => {
      const conflict = conflictOf(item, boot.allergens)
      if (item && conflict && worthAsking(conflict)) setAsk({ item, conflict, run })
      else run()
    },
    [boot.allergens],
  )
  /* By id, for everything already in the pantry. */
  const guarded = useCallback(
    (itemId: string, run: () => void) => askFirst(itemsById.get(itemId), run),
    [itemsById, askFirst],
  )

  /**
   * A food invented in a slot's picker goes into that slot.
   *
   * Unless it no longer fits: the editor can change the category after the
   * fact -- someone adding okra from Monday's veg may decide it is a side --
   * and the server would refuse the placement. The food is still one they
   * wanted, so it stays in the pantry and the app says where it went rather
   * than dropping it somewhere they did not ask for.
   */
  const placeNewFood = useCallback(
    (item: Item, slot: Picking) => {
      if (!canPlace(boot.categories, item.categoryId, slot.category.id)) {
        const filed = boot.categories.find((c) => c.id === item.categoryId)?.name.toLowerCase()
        toast.notify(
          `${item.name} is in your pantry${filed ? ` under ${filed}` : ''}, which does not go in a ${slot.category.name.toLowerCase()} slot.`,
        )
        return
      }
      askFirst(item, () => {
        if (slot.replace) replaceItem(slot.dayOfWeek, slot.replace, item.id)
        else addItem(slot.dayOfWeek, item.id, slot.category.id)
      })
    },
    [boot.categories, toast, askFirst, addItem, replaceItem],
  )

  /**
   * Put a food away from the dock, without the editor.
   *
   * Archive rather than delete: a food the board still has on a day this week
   * would refuse to delete anyway, and "we stopped buying these" is what this
   * is for. The Pantry tab is where it comes back from, so the toast says so.
   */
  const archiveFood = useCallback(
    async (item: Item) => {
      try {
        await api.updateItem(item.id, { archived: true })
        onItemsChanged((await api.bootstrap()).items)
        toast.notify(`Archived ${item.name}. The Pantry tab brings it back.`)
      } catch (err) {
        onError(err, 'Could not archive that food.')
      }
    },
    [onItemsChanged, toast, onError],
  )

  function onDragStart(e: DragStartEvent) {
    const data = e.active.data.current as
      | { kind: 'pantry'; itemId: string }
      | { kind: 'placed'; itemId: string; placementId: string; dayOfWeek: number; categoryId: string }
      | undefined
    if (!data) return
    const item = itemsById.get(data.itemId)
    if (!item) return
    setDragging(
      data.kind === 'pantry'
        ? { kind: 'pantry', item }
        : {
            kind: 'placed',
            item,
            placementId: data.placementId,
            dayOfWeek: data.dayOfWeek,
            categoryId: data.categoryId,
          },
    )
  }

  function onDragEnd(e: DragEndEvent) {
    const active = dragging
    setDragging(null)
    if (!active || !e.over) return

    const target = e.over.data.current as { kind: 'slot'; dayOfWeek: number; categoryId: string } | undefined
    if (target?.kind !== 'slot') return
    if (!canPlace(boot.categories, active.item.categoryId, target.categoryId)) return

    if (active.kind === 'pantry') {
      guarded(active.item.id, () => addItem(target.dayOfWeek, active.item.id, target.categoryId))
    } else if (active.dayOfWeek === target.dayOfWeek) {
      if (active.categoryId !== target.categoryId) {
        refileItem(active.dayOfWeek, active.placementId, target.categoryId)
      }
    } else {
      void moveItem(
        active.dayOfWeek,
        active.placementId,
        target.dayOfWeek,
        active.item.id,
        target.categoryId,
      )
    }
  }

  /* ---- week creation --------------------------------------------------- */

  const previousWeek = useMemo(
    () => weeks.filter((w) => w.startDate < weekStart).sort((a, b) => (a.startDate < b.startDate ? 1 : -1))[0] ?? null,
    [weeks, weekStart],
  )

  /** Empty slots across the week, in day order, skipped days excluded. */
  const gaps = useMemo(() => {
    if (!week) return []
    return week.lunches
      .filter((l) => l.status !== 'skip')
      .flatMap((l) => dayFill(l, boot.categories).empty.map((category) => ({ dayOfWeek: l.dayOfWeek, category })))
  }, [week, boot.categories])

  /*
   * The first week, one choice at a time. With nothing placed yet the board
   * points at Monday's main; once a day is complete and this is the family's
   * only week, it offers to repeat that day. Experienced families see none
   * of this -- the repeat lives in the day menu for them.
   */
  const placed = week ? week.lunches.reduce((n, l) => n + l.items.length, 0) : 0
  const firstDone = week?.lunches.find((l) => l.status !== 'skip' && l.items.length > 0 && dayFill(l, boot.categories).empty.length === 0) ?? null
  const guide: 'start' | 'repeat' | null = !week
    ? null
    : placed === 0
      ? 'start'
      : firstDone && gaps.length > 0 && weeks.length <= 1 && !repeatDismissed
        ? 'repeat'
        : null
  /** The slot the first-week guide points at: the first gap, until a day is complete. */
  const spotlight = week && placed < boot.categories.length && !firstDone ? (gaps[0] ?? null) : null

  /** Jump to the next empty slot from the day in focus and open its picker. */
  function nextEmpty() {
    if (gaps.length === 0) return
    const next = gaps.find((g) => g.dayOfWeek >= focusDay) ?? gaps[0]!
    setFocusDay(next.dayOfWeek)
    setPicker({ dayOfWeek: next.dayOfWeek, category: next.category })
  }

  async function startWeek(copyFromWeekId?: string) {
    if (creating) return
    setCreating(true)
    try {
      const { week: fresh } = await api.ensureWeek(weekStart, copyFromWeekId)
      onWeek(fresh)
      onWeeksChanged()
      toast.notify(copyFromWeekId ? 'Copied last week across.' : 'Week started.')
    } catch (err) {
      onError(err, 'Could not start that week.')
    } finally {
      setCreating(false)
    }
  }

  async function reorder(order: number[]) {
    if (!week) return
    try {
      onWeek(await api.reorderDays(week.id, order))
    } catch (err) {
      onError(err, 'Could not reorder the days.')
    }
  }

  /** Swap two weekdays, moving each day's whole lunch with it. */
  function swapDays(a: number, b: number) {
    void reorder([1, 2, 3, 4, 5].map((d) => (d === a ? b : d === b ? a : d)))
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
      <aside className="pantry-dock">
        <PantryDock
          categories={boot.categories}
          items={boot.items}
          recentIds={recentIds}
          dropTargets={dropTargets}
          onExpand={setViewing}
          onAddFood={(categoryId) =>
            setFoodEdit({ kind: 'new', categoryId: categoryId ?? boot.categories[0]?.id ?? '', name: '', slot: null })
          }
          onEditFood={(item) => setFoodEdit({ kind: 'edit', item })}
          onArchiveFood={archiveFood}
        />
      </aside>

      <div className="content plan-content">
        <div className="week-bar">
          <button className="btn icon" onClick={() => onNavigate(addWeeks(weekStart, -1))} aria-label="Previous week">
            ←
          </button>
          <div className="week-title">
            <strong>{formatWeekRange(weekStart)}</strong>
            <span>{relativeWeekLabel(weekStart)}</span>
          </div>
          <button className="btn icon" onClick={() => onNavigate(addWeeks(weekStart, 1))} aria-label="Next week">
            →
          </button>
          {!isSameWeek(weekStart) && (
            <button className="btn sm" onClick={() => onNavigate(mondayOf())}>
              This week
            </button>
          )}
          {week && (
            <button className="btn sm" onClick={() => setNotesFor({ dayOfWeek: null })}>
              🗒 Week notes
              {week.notes.filter((n) => n.dayOfWeek === null).length > 0 &&
                ` (${week.notes.filter((n) => n.dayOfWeek === null).length})`}
            </button>
          )}
          <div className="spacer" />
          {!loading && week && (
            <ReviewControl
              week={week}
              currentUser={boot.session.name}
              people={boot.people}
              onCompose={() => setComposing(true)}
              onApprove={(comment) => void approveReview(comment)}
              onReopen={() => void reopenReview()}
              busy={reviewBusy}
            />
          )}
        </div>

        {loading && <p className="muted">Loading…</p>}

        {!loading && !week && (
          <div className="empty">
            <span className="big" aria-hidden="true">
              🗓️
            </span>
            <p>Nothing planned for this week yet.</p>
            <div className="row wrap" style={{ justifyContent: 'center', marginTop: 14 }}>
              <button className="btn primary" onClick={() => startWeek()} disabled={creating}>
                Start this week
              </button>
              {previousWeek && (
                <button className="btn" onClick={() => startWeek(previousWeek.id)} disabled={creating}>
                  Copy {formatWeekRange(previousWeek.startDate)}
                </button>
              )}
            </div>
          </div>
        )}

        {guide === 'start' && week && (
          <div className="guide" role="status">
            <div>
              <strong>Start with one lunch {boot.profileName} will eat.</strong>
              <span>Pick a main for Monday. We’ll take it one choice at a time.</span>
            </div>
            <button
              className="btn primary"
              onClick={() => {
                setFocusDay(1)
                setPicker({ dayOfWeek: 1, category: boot.categories[0]! })
              }}
            >
              Choose Monday’s main →
            </button>
          </div>
        )}
        {guide === 'repeat' && firstDone && (
          <div className="guide" role="status">
            <div>
              <strong>{DAY_NAMES[firstDone.dayOfWeek - 1]}’s done. Repeat it across the week?</strong>
              <span>Only empty slots get filled, and you can swap anything after.</span>
            </div>
            <button className="btn primary" onClick={() => setRepeatFrom(firstDone.dayOfWeek)}>
              Repeat {DAY_NAMES[firstDone.dayOfWeek - 1]}…
            </button>
            <button className="btn ghost icon" aria-label="Dismiss" onClick={() => setRepeatDismissed(true)}>
              ✕
            </button>
          </div>
        )}

        {!loading && week && narrow && (
          <div className="day-strip-wrap">
            <div className="day-strip" role="tablist" aria-label="Days">
              {week.lunches.map((lunch) => {
                const fill = dayFill(lunch, boot.categories)
                const off = lunch.status === 'skip'
                const done = !off && fill.empty.length === 0
                return (
                  <button
                    key={lunch.id}
                    type="button"
                    role="tab"
                    aria-selected={focusDay === lunch.dayOfWeek}
                    className={`day-chip ${off ? 'off' : done ? 'done' : 'gap'}`}
                    onClick={() => setFocusDay(lunch.dayOfWeek)}
                  >
                    <span className="day-chip-name">{DAY_NAMES[lunch.dayOfWeek - 1]!.slice(0, 3)}</span>
                    <span className="day-chip-state">
                      {off
                        ? 'Skip'
                        : `${lunch.status === 'pizza' ? '🍕 ' : ''}${done ? '✓' : `${fill.empty.length} gap${fill.empty.length === 1 ? '' : 's'}`}`}
                    </span>
                  </button>
                )
              })}
            </div>
            <div className="day-strip-foot">
              <span className="muted">{gaps.length === 0 ? 'Every slot is filled' : `${gaps.length} slot${gaps.length === 1 ? '' : 's'} left`}</span>
              {gaps.length > 0 && (
                <button type="button" className="linklike" onClick={nextEmpty}>
                  Next empty →
                </button>
              )}
            </div>
          </div>
        )}

        {!loading && week && (
          <div className={`board ${narrow ? 'single' : ''}`}>
            {(narrow ? week.lunches.filter((l) => l.dayOfWeek === focusDay) : week.lunches).map((lunch) => (
              <DayColumn
                key={lunch.id}
                lunch={lunch}
                weekStart={week.startDate}
                categories={boot.categories}
                itemsById={itemsById}
                allergens={boot.allergens}
                dropTargets={dropTargets}
                onAdd={(category) => setPicker({ dayOfWeek: lunch.dayOfWeek, category })}
                onReplace={(placementId, category) => setPicker({ dayOfWeek: lunch.dayOfWeek, category, replace: placementId })}
                onRemove={(placementId) => removeItem(lunch.dayOfWeek, placementId)}
                onRefile={(placementId, categoryId) => refileItem(lunch.dayOfWeek, placementId, categoryId)}
                onStatus={(status) => void setStatus(lunch.dayOfWeek, status)}
                onSettings={() => setSettingsDay(lunch)}
                onRepeat={() => setRepeatFrom(lunch.dayOfWeek)}
                spotlight={spotlight?.dayOfWeek === lunch.dayOfWeek ? spotlight.category.id : null}
                onExpand={setViewing}
                onSwap={swapDays}
                noteCount={week.notes.filter((n) => n.dayOfWeek === lunch.dayOfWeek).length}
                onNotes={() => setNotesFor({ dayOfWeek: lunch.dayOfWeek })}
                onMeals={() => setMealsForDay(lunch.dayOfWeek)}
                siblings={siblings}
                onGiveTo={(dow) =>
                  siblings.length === 1 ? void giveTo(siblings[0]!, dow) : setGiveDay(dow)
                }
              />
            ))}
          </div>
        )}
      </div>

      {ask && (
        <Modal
          title={
            ask.conflict.level === 'severe'
              ? 'Check this one'
              : ask.conflict.rows.some((r) => r.kind === 'diet')
                ? 'Against a restriction you set'
                : 'Not allowed at school'
          }
          onClose={() => setAsk(null)}
          footer={
            <>
              <button className="btn ghost" onClick={() => setAsk(null)}>
                Pick something else
              </button>
              <div className="spacer" />
              <button
                className="btn primary"
                onClick={() => {
                  ask.run()
                  setAsk(null)
                }}
              >
                Add it anyway
              </button>
            </>
          }
        >
          <div className={`clash-note ${ask.conflict.level}`}>
            <strong>{ask.item.name}</strong>
            <span>{conflictLine(ask.conflict, boot.profileName)}</span>
          </div>
          {/*
            * "Add it anyway" is a real option, not a dark pattern. The app
            * knows a tag on a food, not what is in the cupboard: the
            * gluten-free version of a food is still tagged wheat, and a
            * parent who bought it should not have to fight the board.
            */}
          <p className="muted small">
            If you have the version that is fine for {boot.profileName || 'your child'}, add it. {CHECK_THE_PACKET}
          </p>
        </Modal>
      )}

      {viewing?.imageKey && (
        <ImageLightbox
          src={imageUrl(viewing.imageKey)}
          alt={viewing.name}
          onClose={() => setViewing(null)}
        />
      )}

      <DragOverlay dropAnimation={null}>
        {dragging && (
          <div className="drag-ghost">
            <Thumb emoji={dragging.item.emoji} imageKey={dragging.item.imageKey} name={dragging.item.name} />
            {dragging.item.name}
          </div>
        )}
      </DragOverlay>

      {picker && (
        <ItemPicker
          category={picker.category}
          recentIds={recentIds}
          initialQuery={picker.query}
          title={`${DAY_NAMES[picker.dayOfWeek - 1]} · ${picker.replace ? 'Replace' : 'Add'} ${picker.category.name.toLowerCase()}`}
          items={boot.items.filter(
            (i) => !i.archived && canPlace(boot.categories, i.categoryId, picker.category.id),
          )}
          onPick={(itemId) => {
            const { dayOfWeek, category, replace } = picker
            setPicker(null)
            guarded(itemId, () => {
              if (replace) replaceItem(dayOfWeek, replace, itemId)
              else addItem(dayOfWeek, itemId, category.id)
            })
          }}
          onCreate={(name) => {
            const slot = { ...picker, query: name }
            pickerBehind.current = slot
            setPicker(null)
            setFoodEdit({ kind: 'new', categoryId: picker.category.id, name: asFoodName(name), slot })
          }}
          onClose={() => setPicker(null)}
        />
      )}

      {notesFor && week && (
        <NotesPanel
          dayOfWeek={notesFor.dayOfWeek}
          title={
            notesFor.dayOfWeek === null
              ? `Notes — week of ${formatWeekRange(week.startDate)}`
              : `Notes — ${DAY_NAMES[notesFor.dayOfWeek - 1]}, ${formatDayDate(week.startDate, notesFor.dayOfWeek)}`
          }
          notes={week.notes}
          currentUser={boot.session.name}
          people={boot.people.map((p) => p.name)}
          onAdd={async (body) => {
            try {
              onWeek(await api.addNote(week.id, notesFor.dayOfWeek, body))
            } catch (err) {
              onError(err, 'Could not add that note.')
            }
          }}
          onEdit={async (noteId, body) => {
            try {
              onWeek(await api.updateNote(week.id, noteId, body))
            } catch (err) {
              onError(err, 'Could not update that note.')
            }
          }}
          onDelete={async (noteId) => {
            try {
              onWeek(await api.deleteNote(week.id, noteId))
            } catch (err) {
              onError(err, 'Could not delete that note.')
            }
          }}
          onClose={() => setNotesFor(null)}
        />
      )}

      {composing && week && (
        <ReviewComposer
          week={week}
          currentUser={boot.session.name}
          people={boot.people}
          emailConfigured={boot.emailConfigured}
          busy={reviewBusy}
          onSend={(payload) => void requestReview(payload)}
          onClose={() => setComposing(false)}
        />
      )}

      {mealsForDay !== null && week && (
        <MealPicker
          lunch={week.lunches.find((l) => l.dayOfWeek === mealsForDay)!}
          meals={boot.meals}
          items={boot.items}
          categories={boot.categories}
          onApply={(mealId, mode) => {
            void applyMeal(mealsForDay, mealId, mode)
            setMealsForDay(null)
          }}
          onSaveDay={
            week.lunches.find((l) => l.dayOfWeek === mealsForDay)!.items.length > 0
              ? (name) => saveDayAsMeal(mealsForDay, name)
              : null
          }
          onToggleFavorite={(meal) => void toggleFavorite(meal)}
          onManage={() => {
            setMealsForDay(null)
            onManageMeals()
          }}
          onClose={() => setMealsForDay(null)}
        />
      )}

      {repeatFrom !== null && week && week.lunches.some((l) => l.dayOfWeek === repeatFrom) && (
        <RepeatDay
          week={week}
          source={week.lunches.find((l) => l.dayOfWeek === repeatFrom)!}
          categories={boot.categories}
          itemsById={itemsById}
          busy={repeatBusy}
          onApply={(days) => void repeatDay(repeatFrom, days)}
          onClose={() => setRepeatFrom(null)}
        />
      )}

      {/* Which sibling, when there is more than one to choose from. */}
      {giveDay !== null && (
        <Modal title={`Give ${DAY_NAMES[giveDay - 1]} to…`} onClose={() => setGiveDay(null)}>
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 13.5 }}>
            Their board for this week will hold what {boot.profileName} is having. Anything
            their own allergies or school rules have something to say about will be checked
            first.
          </p>
          <div className="row wrap">
            {siblings.map((k) => (
              <button key={k.id} className="btn primary" onClick={() => void giveTo(k, giveDay)}>
                {k.name}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/*
        The receiving child's own allergens, restrictions and school rules
        speaking. The same question the board asks when a food is dropped on a
        day, asked once for the whole lunch.
      */}
      {giving && (
        <Modal
          title={`Check this for ${giving.child.name}`}
          onClose={() => setGiving(null)}
          footer={
            <>
              <button className="btn ghost" onClick={() => setGiving(null)}>
                Don’t
              </button>
              <div className="spacer" />
              <button
                className="btn danger"
                onClick={() => void giveTo(giving.child, giving.dayOfWeek, true)}
              >
                Give it to them anyway
              </button>
            </>
          }
        >
          <ul className="give-clashes">
            {giving.clashes.map((c) => (
              <li key={`${c.dayOfWeek}:${c.itemId}`} className={c.stopping ? 'stopping' : ''}>
                <span className="give-day">{DAY_NAMES[c.dayOfWeek - 1]}</span>
                <span className="give-food">{c.name}</span>
                <span className="give-why">{c.reasons.join(', ')}</span>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ fontSize: 12.5, margin: '10px 0 0' }}>
            {CHECK_THE_PACKET}
          </p>
        </Modal>
      )}

      {foodEdit !== null && (
        <ItemEditor
          categories={boot.categories}
          item={foodEdit.kind === 'edit' ? foodEdit.item : null}
          defaultCategoryId={foodEdit.kind === 'edit' ? foodEdit.item.categoryId : foodEdit.categoryId}
          defaultName={foodEdit.kind === 'edit' ? '' : foodEdit.name}
          watchFor={boot.allergens}
          onSaved={async (saved) => {
            const slot = foodEdit.kind === 'new' ? foodEdit.slot : null
            pickerBehind.current = null
            /*
             * The pantry first, then the slot. A placement whose food the
             * board has not heard of yet renders as nothing at all (see
             * Slot), so the order here is the difference between the tile
             * appearing and the tile blinking into existence a moment later.
             */
            try {
              onItemsChanged((await api.bootstrap()).items)
            } catch (err) {
              onError(err, 'Saved, but the pantry did not refresh. Reload to see it.')
            }
            if (saved && slot) placeNewFood(saved, slot)
          }}
          onClose={() => {
            const back = pickerBehind.current
            pickerBehind.current = null
            setFoodEdit(null)
            if (back) setPicker(back)
          }}
          onError={onError}
        />
      )}

      {settingsDay && week && (
        <DaySettings
          lunch={settingsDay}
          weekStart={week.startDate}
          onSave={async (patch) => {
            try {
              onWeek(await api.setDay(week.id, settingsDay.dayOfWeek, patch))
              setSettingsDay(null)
            } catch (err) {
              onError(err, 'Could not update that day.')
            }
          }}
          onSwap={(other) => {
            swapDays(settingsDay.dayOfWeek, other)
            setSettingsDay(null)
          }}
          onClose={() => setSettingsDay(null)}
        />
      )}
    </DndContext>
  )
}

/**
 * Categories sharing a `container` are packed into the same physical box, so
 * consecutive ones are grouped and the board draws a break between groups.
 */
function groupByContainer(categories: Category[]) {
  const groups: { container: string; categories: Category[] }[] = []
  for (const category of categories) {
    const current = groups[groups.length - 1]
    if (current && current.container === category.container) current.categories.push(category)
    else groups.push({ container: category.container, categories: [category] })
  }
  return groups
}

/* ---- Day column --------------------------------------------------------- */

interface DayProps {
  lunch: Lunch
  weekStart: string
  categories: Category[]
  itemsById: Map<string, Item>
  allergens: ProfileAllergen[]
  dropTargets: Set<string> | null
  onAdd: (category: Category) => void
  onReplace: (placementId: string, category: Category) => void
  onRemove: (placementId: string) => void
  onRefile: (placementId: string, categoryId: string) => void
  onStatus: (status: DayStatus) => void
  onSettings: () => void
  onRepeat: () => void
  /** The category the first-week guide is pointing at on this day, if any. */
  spotlight: string | null
  onSwap: (a: number, b: number) => void
  onExpand: (item: Item) => void
  noteCount: number
  onNotes: () => void
  onMeals: () => void
  /** The other children in the family, for "give this lunch to". */
  siblings: Child[]
  onGiveTo: (dayOfWeek: number) => void
}

/** Physical containers get a glyph each; the first is the lunchbox, the rest are bags. */
const CONTAINER_ICONS = ['🧺', '🎒', '👜', '🧃']

/**
 * How full a day is: categories with at least one food, out of all of them.
 * A pizza day's main is covered by the school, so it counts.
 */
export function dayFill(lunch: Lunch, categories: Category[]): { filled: number; total: number; empty: Category[] } {
  const main = categories[0]
  const empty = categories.filter(
    (c) => !lunch.items.some((i) => i.categoryId === c.id) && !(lunch.status === 'pizza' && c.id === main?.id),
  )
  return { filled: categories.length - empty.length, total: categories.length, empty }
}

function DayColumn({
  lunch,
  weekStart,
  categories,
  itemsById,
  allergens,
  dropTargets,
  onAdd,
  onReplace,
  onRemove,
  onRefile,
  onStatus,
  onSettings,
  onRepeat,
  spotlight,
  onSwap,
  onExpand,
  noteCount,
  onNotes,
  onMeals,
  siblings,
  onGiveTo,
}: DayProps) {
  const dayName = DAY_NAMES[lunch.dayOfWeek - 1]!
  const off = lunch.status === 'skip'
  const fill = dayFill(lunch, categories)
  /*
   * Only the loud ones are counted. A day badge that lights up for every mild
   * dislike is a badge nobody reads, and the quiet ones are already marked on
   * the tile itself.
   */
  const clashes = off
    ? 0
    : lunch.items.filter((i) => {
        const c = conflictOf(itemsById.get(i.itemId), allergens)
        return c !== null && worthAsking(c)
      }).length
  const status = DAY_STATUSES.find((s) => s.value === lunch.status)!

  const more: MenuItem[] = [
    { label: 'Fill from a saved meal', icon: '⭐', onSelect: onMeals },
    { label: 'Repeat this lunch on…', icon: '⧉', onSelect: onRepeat, disabled: off || lunch.items.length === 0 },
    /*
     * Only when there is a sibling to give it to. The same sandwich twice is
     * what a second lunchbox usually is, so it sits next to the one that
     * repeats a lunch onto another day.
     */
    ...(siblings.length > 0
      ? [
          {
            label: siblings.length === 1 ? `Give this lunch to ${siblings[0]!.name}` : 'Give this lunch to…',
            icon: '🧒',
            /* A pizza day is a plan too, and for two children at the same
               school it is usually the same plan twice, so an empty one is
               still worth handing over. An empty packing day is not. */
            disabled: off || (lunch.items.length === 0 && lunch.status === 'packing'),
            onSelect: () => onGiveTo(lunch.dayOfWeek),
          } as MenuItem,
        ]
      : []),
    { label: noteCount ? `Notes (${noteCount})` : 'Add a note', icon: '🗒', onSelect: onNotes },
    { kind: 'divider' },
    { label: 'Move earlier', icon: '◀', onSelect: () => onSwap(lunch.dayOfWeek, lunch.dayOfWeek - 1), disabled: lunch.dayOfWeek === 1 },
    { label: 'Move later', icon: '▶', onSelect: () => onSwap(lunch.dayOfWeek, lunch.dayOfWeek + 1), disabled: lunch.dayOfWeek === 5 },
    { label: 'Day settings…', icon: '⚙', hint: 'label, swap', onSelect: onSettings },
  ]

  return (
    <section className={`day ${off ? 'is-off' : ''}`} aria-label={dayName}>
      <header className="day-head">
        <div className="day-name">
          <strong>{dayName}</strong>
          <span title={formatDayDate(weekStart, lunch.dayOfWeek)}>{dayDate(weekStart, lunch.dayOfWeek).getDate()}</span>
          {clashes > 0 && (
            <span className="day-clash" title={`${clashes} to check on ${dayName}`}>
              <span aria-hidden="true">⚠️</span>
              <span className="sr-only">{`${clashes} to check on ${dayName}`}</span>
              {clashes}
            </span>
          )}
          <div className="spacer" />
          <Menu trigger="…" label={`More for ${dayName}`} items={more} className="btn ghost icon day-more" align="right" />
        </div>
        <div className="day-tools">
          <Menu
            trigger={
              <>
                {status.emoji} {status.value === 'packing' ? 'Packing' : status.value === 'pizza' ? 'Pizza day' : 'Skip'}{' '}
                <span className="caret" aria-hidden="true">
                  ▾
                </span>
              </>
            }
            label={`How ${dayName} is handled`}
            className={`status-pill ${lunch.status}`}
            items={DAY_STATUSES.map((o) => ({ label: o.label, icon: o.emoji, active: o.value === lunch.status, onSelect: () => onStatus(o.value) }))}
          />
          <div className="spacer" />
          {!off && (
            <span className={`day-fill ${fill.empty.length === 0 ? 'done' : ''}`} title={`${fill.filled} of ${fill.total} slots filled`}>
              {fill.empty.length === 0 ? '✓' : `${fill.filled}/${fill.total}`}
            </span>
          )}
        </div>
        {/* A skipped day repeats its note in the body, so only show it here otherwise. */}
        {lunch.label && lunch.status !== 'skip' && <div className="day-label">{lunch.label}</div>}
      </header>

      {off ? (
        <div className="day-off-note">
          <span className="big" aria-hidden="true">
            🚫
          </span>
          {lunch.label || 'No lunch needed'}
        </div>
      ) : (
        <div className="day-body">
          {groupByContainer(categories).map((group, gi) => (
            <div className="slot-group" key={group.container}>
              <div className="slot-group-head">
                <span aria-hidden="true">{CONTAINER_ICONS[gi] ?? CONTAINER_ICONS[1]}</span> {group.container}
              </div>
              {group.categories.map((category) => (
                <Slot
                  key={category.id}
                  category={category}
                  categories={categories}
                  dayOfWeek={lunch.dayOfWeek}
                  status={lunch.status}
                  isMain={category.id === categories[0]?.id}
                  placements={lunch.items.filter((i) => i.categoryId === category.id)}
                  itemsById={itemsById}
                  allergens={allergens}
                  dimmed={dropTargets !== null && !dropTargets.has(category.id)}
                  highlight={spotlight === category.id}
                  onAdd={() => onAdd(category)}
                  onReplace={(placementId) => onReplace(placementId, category)}
                  onRemove={onRemove}
                  onRefile={onRefile}
                  onExpand={onExpand}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {!off && (
        <footer className="day-foot">
          <span className="muted">
            {fill.empty.length === 0 ? 'All choices filled' : `${fill.empty.length} empty slot${fill.empty.length === 1 ? '' : 's'}`}
          </span>
          {noteCount > 0 && (
            <button type="button" className="linklike day-note-link" onClick={onNotes}>
              🗒 {noteCount} note{noteCount === 1 ? '' : 's'}
            </button>
          )}
        </footer>
      )}
    </section>
  )
}

/* ---- Category slot ------------------------------------------------------ */

interface SlotProps {
  category: Category
  categories: Category[]
  dayOfWeek: number
  status: DayStatus
  isMain: boolean
  placements: Lunch['items']
  itemsById: Map<string, Item>
  /** The child's allergens and the school's rules, to flag what they hit. */
  allergens: ProfileAllergen[]
  dimmed: boolean
  /** The first-week guide's "start here" ring. */
  highlight: boolean
  onAdd: () => void
  onReplace: (placementId: string) => void
  onRemove: (placementId: string) => void
  onRefile: (placementId: string, categoryId: string) => void
  onExpand: (item: Item) => void
}

/**
 * One category of one day. Empty, it is a dashed "Add fruit" tile; filled, it
 * is the food itself with the category as a subtitle -- the food is the thing
 * being decided, so the food gets the type size. More than one food in a slot
 * stacks inside the same tile.
 */
function Slot({ category, categories, dayOfWeek, status, isMain, placements, itemsById, allergens, dimmed, highlight, onAdd, onReplace, onRemove, onRefile, onExpand }: SlotProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot:${dayOfWeek}:${category.id}`,
    data: { kind: 'slot', dayOfWeek, categoryId: category.id },
    disabled: dimmed,
  })
  const dayName = DAY_NAMES[dayOfWeek - 1]!
  // The other slot this food could be refiled into: Side <-> Snack.
  const siblings = categories.filter((c) => c.id !== category.id && c.interchangeableGroup && c.interchangeableGroup === category.interchangeableGroup)

  const cls = `slot ${isOver ? 'over' : ''} ${placements.length > 0 ? 'filled' : 'vacant'}`
  const style = dimmed ? { opacity: 0.4 } : undefined

  if (placements.length === 0) {
    if (status === 'pizza' && isMain) {
      return (
        <div ref={setNodeRef} className={`${cls} covered`} style={style}>
          <div className="tile static">
            <span className="thumb" aria-hidden="true">
              🍕
            </span>
            <span className="tile-text">
              <span className="tile-name">School pizza</span>
              <span className="tile-sub">{category.name} · covered</span>
            </span>
          </div>
        </div>
      )
    }
    return (
      <div ref={setNodeRef} className={cls} style={style}>
        <button type="button" className={`tile add ${highlight ? 'next' : ''}`} onClick={onAdd} aria-label={`Add ${category.name} to ${dayName}`}>
          <span className="plus" aria-hidden="true">
            +
          </span>
          {highlight ? `Choose a ${category.name.toLowerCase()}` : `Add ${category.name.toLowerCase()}`}
          {highlight && <span className="next-badge">Start here</span>}
        </button>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} className={cls} style={style}>
      {placements.map((placement, i) => {
        const item = itemsById.get(placement.itemId)
        if (!item) return null
        return (
          <FoodTile
            key={placement.id}
            item={item}
            conflict={conflictOf(item, allergens)}
            placementId={placement.id}
            dayOfWeek={dayOfWeek}
            categoryId={category.id}
            sub={i === 0 ? (placements.length > 1 ? `${category.name} · ${placements.length} foods` : category.name) : ''}
            menu={[
              { label: 'Replace…', icon: '↻', onSelect: () => onReplace(placement.id) },
              { label: `Add another ${category.name.toLowerCase()}…`, icon: '+', onSelect: onAdd },
              ...siblings.map((c) => ({ label: `Move to ${c.name.toLowerCase()}`, icon: '⇄', onSelect: () => onRefile(placement.id, c.id) })),
              { kind: 'divider' as const },
              { label: 'Remove', icon: '✕', danger: true, onSelect: () => onRemove(placement.id) },
            ]}
            onRemove={() => onRemove(placement.id)}
            onExpand={onExpand}
          />
        )
      })}
      <button type="button" className="slot-more" onClick={onAdd} aria-label={`Add another ${category.name} to ${dayName}`} title="Add another">
        +
      </button>
    </div>
  )
}

function FoodTile({
  item,
  placementId,
  dayOfWeek,
  categoryId,
  sub,
  conflict,
  menu,
  onRemove,
  onExpand,
}: {
  item: Item
  placementId: string
  dayOfWeek: number
  /** The slot it is in now, which is not always the item's own category. */
  categoryId: string
  sub: string
  /** What this food runs into, or null for nothing we were told about. */
  conflict: Conflict | null
  menu: MenuItem[]
  onRemove: () => void
  onExpand: (item: Item) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `placed:${placementId}`,
    data: { kind: 'placed', itemId: item.id, placementId, dayOfWeek, categoryId },
  })

  return (
    /*
     * The whole tile is the drag target; the keyboard handle is the tile too.
     * The × and the menu opt out with data-no-drag so a press on them is a
     * press, not a pick-up.
     */
    <div
      ref={setNodeRef}
      className={`tile ${isDragging ? 'dragging' : ''} ${sub ? '' : 'compact'} ${conflict ? `clash ${conflict.level}` : ''}`}
      {...listeners}
      {...attributes}
      title={[item.notes, conflict && conflictShort(conflict)].filter(Boolean).join(' · ') || undefined}
    >
      <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} onExpand={() => onExpand(item)} />
      <span className="tile-text">
        <span className="tile-name">
          {conflict && (
            <span className="clash-mark" role="img" aria-label={conflictShort(conflict)}>
              {conflict.level === 'severe' ? '⚠️' : conflict.level === 'rule' ? '🚫' : '•'}
            </span>
          )}
          {item.name}
        </span>
        {sub && <span className="tile-sub">{sub}</span>}
      </span>
      <span className="tile-actions" data-no-drag>
        <button type="button" className="x" onClick={onRemove} aria-label={`Remove ${item.name}`} title="Remove">
          ✕
        </button>
        <Menu trigger="⋯" label={`Options for ${item.name}`} items={menu} className="tile-menu" align="right" />
      </span>
    </div>
  )
}
